import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Outlet, isRouteErrorResponse } from "react-router";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";

import { Html } from "#app/components/html.tsx";
import {
  NotFoundPage,
  UnknownErrorPage,
} from "#app/components/error-pages.tsx";
import styles from "#app/styles/tailwind.css?url";

import { honeypot } from "./services/honeypot.server.ts";
import { getPublicEnv } from "./services/env.server";
import type { Route } from "./+types/root.ts";

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: styles },
];

export const ErrorBoundary = ({
  loaderData,
  error,
}: Route.ErrorBoundaryProps) => {
  const htmlProps = { env: loaderData?.env, nonce: loaderData?.nonce ?? "" };
  const isErrorResp = isRouteErrorResponse(error);

  if (isErrorResp && error.status === 404) {
    return <NotFoundPage {...htmlProps} />;
  }

  // only capture non-404 errors
  if (!isErrorResp && error instanceof Error) {
    Sentry.captureException(error);
  }

  return <UnknownErrorPage error={error} {...htmlProps} />;
};

export async function loader({ context }: Route.LoaderArgs) {
  const honeyProps = await honeypot.getInputProps();
  const env = getPublicEnv();

  return { honeyProps, env, nonce: context.cspNonce };
}

export default function App({ loaderData }: Route.ComponentProps) {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <HoneypotProvider {...loaderData.honeyProps}>
        <Html env={loaderData.env} nonce={loaderData.nonce}>
          <Outlet />
        </Html>
      </HoneypotProvider>
    </QueryClientProvider>
  );
}
